import { ReactNode, useCallback, useMemo, useRef, useState, useEffect } from "react";
import styles from "@/pages/index.module.css";
import { ReactSketchCanvas, type ReactSketchCanvasRef } from "react-sketch-canvas";
import { Colors, ColorPicker } from "@/components/ColorPicker";
import { Logo } from "@/components/Logo";
import { Ad } from "@/components/Ad";
import { Timer } from "@/components/Timer";
import { PigeonSpeech } from "@/components/PigeonSpeech";
import { Pigeon } from "@/components/Pigeon";
import { getTodaysPrompt } from "@/utils/prompts";
import { Step } from "@/typings/step";
import { Direction } from "@/components/Direction";
import { Guess, GuessResponse } from "@/typings/guess";
import { resizeImageForExport } from "@/utils/image";
import { ClearAction } from "@/components/ClearAction";
import { isSuccess } from "@/utils/scoring";
import { useTodaysResult } from "@/components/useTodaysResults";
import noteUrl from "@/images/note.svg";
import { HowToPlay } from "@/components/HowToPlay";
import { About } from "@/components/About";
import { News } from "@/components/News";
import { Outlet } from "react-router";

export default function HomePage(): ReactNode {
  const prompt = useMemo(() => {
    return getTodaysPrompt();
  }, []);

  const { todaysResult, todaysLastStep, storeTodaysResult } = useTodaysResult();

  const [previousGuesses, setPreviousGuesses] = useState<Array<Guess>>(todaysResult ?? []);
  const guessPictureContents = useCallback(async () => {
    if (canvasRef.current === null) {
      return;
    }

    const rawImageBase64Str = await canvasRef.current.exportImage("png");
    const resizedImageBase64Str = await resizeImageForExport(rawImageBase64Str);

    const response = await fetch("api/guess", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        image: resizedImageBase64Str,
        prompt,
        round: previousGuesses.length + 1,
        previousGuesses: previousGuesses.map((previousGuess) => ({
          guess: previousGuess.guess,
          score: previousGuess.score,
        })),
      }),
    });

    const responseJson: GuessResponse = await response.json();
    const canvasPaths = await canvasRef.current.exportPaths();

    const newPreviousGuesses = [
      ...previousGuesses,
      { ...responseJson, image: rawImageBase64Str, canvasPaths },
    ];
    setPreviousGuesses(newPreviousGuesses);
    storeTodaysResult(newPreviousGuesses);

    if (isSuccess(responseJson.score)) {
      setStep(Step.Success);
    } else {
      setStep((prevStep) => {
        switch (prevStep) {
          case Step.RoundOneGuessing:
            return Step.RoundOneFail;
          case Step.RoundTwoGuessing:
            return Step.RoundTwoFail;
          default:
            return Step.RoundThreeFail;
        }
      });
    }
  }, [previousGuesses]);

  const [step, setStep] = useState(todaysLastStep ?? Step.Start);
  useEffect(() => {
    if (previousGuesses.length > 0 && canvasRef.current !== null) {
      const previousPaths = previousGuesses[previousGuesses.length - 1].canvasPaths;
      if (previousPaths && previousPaths.length > 0) {
        canvasRef.current.loadPaths(previousPaths);
      }
    }
  }, []);

  const nextStepTimeout = useRef<NodeJS.Timeout | null>(null);
  const updateStep = useCallback((newStep: Step) => {
    setStep(newStep);

    if (nextStepTimeout.current) {
      clearTimeout(nextStepTimeout.current);
      nextStepTimeout.current = null;
    }

    switch (newStep) {
      case Step.RoundOne:
        nextStepTimeout.current = setTimeout(() => {
          setStep(Step.RoundOneGuessing);
        }, 20000);
        break;

      case Step.RoundTwo:
        nextStepTimeout.current = setTimeout(() => {
          setStep(Step.RoundTwoGuessing);
        }, 20000);
        break;

      case Step.RoundThree:
        nextStepTimeout.current = setTimeout(() => {
          setStep(Step.RoundThreeGuessing);
        }, 20000);
        break;
    }
  }, []);

  useEffect(() => {
    if ([Step.RoundOneGuessing, Step.RoundTwoGuessing, Step.RoundThreeGuessing].includes(step)) {
      guessPictureContents();
    }
  }, [step, previousGuesses, guessPictureContents]);

  const clearDrawing = useCallback(() => {
    if (canvasRef.current === null) {
      return;
    }

    canvasRef.current.clearCanvas();
  }, []);

  const canvasRef = useRef<ReactSketchCanvasRef | null>(null);
  const [selectedColor, setSelectedColor] = useState(Colors[0]);

  const isLoading = [
    Step.RoundOneGuessing,
    Step.RoundTwoGuessing,
    Step.RoundThreeGuessing,
    Step.RoundOneFail,
    Step.RoundTwoFail,
    Step.RoundThreeFail,
    Step.Success,
  ].includes(step);

  const [isDone, setIsDone] = useState([Step.RoundThreeFail, Step.Success].includes(step));
  useEffect(() => {
    if ([Step.RoundThreeFail, Step.Success].includes(step)) {
      setTimeout(() => {
        setIsDone(true);
      }, 500);
    }
  }, [step]);

  return (
    <main className={styles.background}>
      <div className={styles.body}>
        <div className={styles.logoLayout}>
          <Logo />
        </div>
        <div className={styles.colorPickerLayout}>
          <ColorPicker selectedColor={selectedColor} onSelect={setSelectedColor} />
        </div>
        <div className={styles.timerLayout}>
          <Timer step={step} onUpdateStep={updateStep} />
        </div>
        <div className={styles.canvasLayout}>
          <ClearAction step={step} onClear={clearDrawing} />
          <Direction
            step={step}
            prompt={prompt}
            previousGuesses={previousGuesses}
            onUpdateStep={updateStep}
          />
          <ReactSketchCanvas
            style={{
              border: "3px solid #bbbbbb",
              borderRadius: "3px",
            }}
            backgroundImage={noteUrl}
            ref={canvasRef}
            className={`${styles.canvas} ${isLoading ? styles.canvasLoading : ""} ${isDone ? styles.canvasDone : ""}`}
            width="500"
            height="500"
            strokeWidth={4}
            strokeColor={selectedColor}
          />
        </div>
        <div className={styles.pigeonSpeechLayout}>
          <PigeonSpeech step={step} previousGuesses={previousGuesses} onUpdateStep={updateStep} />
        </div>
        <div className={styles.adLayout}>
          <Ad />
        </div>
        <div className={styles.pigeonLayout}>
          <Pigeon step={step} previousGuesses={previousGuesses} />
        </div>
      </div>
      <div className={styles.description}>
        <div className={styles.newsLayout}>
          <News />
        </div>
        <div className={styles.howToPlayLayout}>
          <HowToPlay />
          <About />
        </div>
      </div>
      <Outlet />
    </main>
  );
}
